import { GlobeIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";

/** Brand marks rendered via `simplydo/react/ui/brand-icon` (not Lucide). */
export type SocialBrandName = "facebook" | "linkedin" | "instagram" | "github";

export const maskEmailAddress = (email: string | undefined | null) => {
  if (!email) return "****";
  const at = email.lastIndexOf("@");
  if (at <= 0) return "****";
  return `****${email.slice(at)}`;
};

export const getUserDomain = (
  user: Pick<OpenAPI.Schemas["User"], "emails">,
) => {
  if (!user) {
    return;
  }

  if (user.emails.length === 0) {
    return;
  }

  const email = user.emails[0];
  if (!email || !email.verified) {
    return;
  }

  return email.address.split("@")[1];
};

export const isCommonDomain = (domain: string) => {
  const domainParts = domain.split(".");
  if (!domainParts.length) {
    return false;
  }
  const domainProvider = domainParts[0];
  if (!domainProvider) {
    return false;
  }

  if (commonDomainProviders.includes(domainProvider)) {
    return true;
  }

  for (const ending of commonDomainEndings) {
    if (domain.endsWith(ending)) {
      return true;
    }
  }
};

export const profileLinks = {
  facebook: {
    brand: "facebook" as const,
    title: "Facebook",
    link: "https://facebook.com/",
  },
  twitter: { iconLeftAlt: "X", title: "𝕏.com", link: "https://twitter.com/" },
  instagram: {
    brand: "instagram" as const,
    title: "Instagram",
    link: "https://instagram.com/",
  },
  github: {
    brand: "github" as const,
    title: "Github",
    link: "https://github.com/",
  },
  linkedin: {
    brand: "linkedin" as const,
    title: "LinkedIn",
    link: "https://linkedin.com/in/",
  },
};

export const socialMediaLinks = {
  facebook: {
    brand: "facebook" as const,
    content: "Facebook",
    link: "https://facebook.com/",
    color: "#1877F2",
  },
  twitter: {
    iconLeftAlt: "𝕏",
    content: "𝕏.com",
    link: "https://twitter.com/",
    color: "#1DA1F2",
  },
  instagram: {
    brand: "instagram" as const,
    content: "Instagram",
    link: "https://instagram.com/",
    color: "#C13584",
  },
  github: {
    brand: "github" as const,
    content: "Github",
    link: "https://github.com/",
    color: "#333",
  },
  linkedin: {
    brand: "linkedin" as const,
    content: "LinkedIn",
    link: "https://linkedin.com/in/",
    color: "#0077B5",
  },
  website: { icon: GlobeIcon, content: "Website", link: "", color: "#000" },
};

export const commonDomainEndings = [
  ".ac.uk",
  ".edu",
  "nhs.net",
  "cp.catapult.org.uk",
  "digicatapult.org.uk",
  "gov.uk",
  "nhs.uk",
];

export const commonDomainProviders = [
  "gmail",
  "yahoo",
  "outlook",
  "hotmail",
  "aol",
  "icloud",
  "zoho",
  "mail",
  "gmx",
  "protonmail",
  "rocketmail",
  "googlemail",
  "live",
  "rediffmail",
  "rediff",
  "msn",
  "att",
  "sbcglobal",
  "verizon",
  "comcast",
  "charter",
  "optonline",
  "earthlink",
  "cox",
  "frontier",
  "windstream",
  "btinternet",
  "talktalk",
  "sky",
  "virginmedia",
  "blueyonder",
  "ntlworld",
  "tiscali",
  "zen",
  "plus",
  "uk2",
  "web",
  "freenet",
  "t-online",
  "orange",
  "sfr",
  "laposte",
  "libero",
  "virgilio",
  "alice",
  "tin",
  "wanadoo",
  "free",
  "online",
  "ziggo",
  "xs4all",
  "rogers",
  "bell",
  "sympatico",
  "telus",
  "shaw",
  "cogeco",
  "mts",
  "eastlink",
  "sasktel",
  "videotron",
  "bigpond",
  "optusnet",
  "iinet",
  "dodo",
  "internode",
  "tpg",
  "westnet",
  "adam",
  "primus",
  "netspace",
  "qq",
  "163",
  "126",
  "sina",
  "naver",
  "hanmail",
  "daum",
  "yahoo",
  "mail",
  "yandex",
  "uol",
  "terra",
  "bol",
  "oi",
  "speedy",
  "fibertel",
  "arnet",
  "prodigy",
  "telmex",
  "maktoob",
  "arabia",
  "naseej",
  "sawasdee",
  "dubaimail",
  "emarati",
  "mweb",
  "vodamail",
  "webmail",
  "kenyaweb",
  "afrihost",
  "fastmail",
  "hushmail",
  "runbox",
  "tutanota",
  "startmail",
  "yopmail",
  "temp-mail",
  "email",
  "inbox",
];
